import React, { useRef, useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Button from '../../../../components/Button';
import ImageUpload from '../../../../components/ImageUpload';
import FileUpload from '../../../../components/FileUpload';
import Modal from '../../../../components/Modal';
import Toast from '../../../../components/Toast';
import TableBox from '../../../../components/TableBox';
import PropertyService from '../../../../services/property.service';
import CommonService from '../../../../services/common.service';
import useFetch from '../../../../hooks/useFetch';
import { VALIDATION_FIELD_MAPPING, GENERIC_LOOKUP_KEYS } from '../../../../utils/appConstant';
import { HeadCellType, TableContextProvider } from '../../../../interfaces/common';

import DocumentRow from './DocumentRow';

interface Props {
    formData: any;
    onChange: (value: any, name: string) => void;
    view: boolean;
    onRefresh?: () => void;
    errors?: any;
}


const FilesTab: React.FC<Props> = ({ formData, onChange, view, onRefresh, errors }) => {
    const { t } = useTranslation();
    const { id } = useParams<{ id: string }>();
    const galleryInputRef = useRef<HTMLInputElement>(null);
    const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; type: string | null; id: number | null; item?: any }>({
        open: false,
        type: null,
        id: null
    });
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [selectedGalleryItems, setSelectedGalleryItems] = useState<number[]>([]);
    const [selectedDocumentItems, setSelectedDocumentItems] = useState<any[]>([]);

    const { data: documentTypes } = useFetch({
        service: CommonService,
        func: 'genericLookup',
        payload: { key: GENERIC_LOOKUP_KEYS.documentType, searchText: '' }, 
        dependency: [],
    });

    const handleGalleryMultipleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            const newFiles = Array.from(e.target.files);
            const currentGallery = formData.gallery || [];
            onChange([...currentGallery, ...newFiles], VALIDATION_FIELD_MAPPING.gallery || 'gallery');
        }
        e.target.value = '';
    };

    const handleRemoveFile = (field: string) => {
        const fileData = formData[field];
        if (fileData instanceof File) {
             onChange(null, field);
             return;
        }

        const docId = fileData?.documents?.[0]?.id;
        
        if (docId) {
            setDeleteConfirm({ open: true, type: field, id: docId });
        } else {
            onChange(null, field);
        }
    };

    const handleSingleDelete = (item: any) => {
        if (item.id) {
            setDeleteConfirm({ open: true, type: 'single-document', id: item.id });
        } else {
            setDeleteConfirm({ open: true, type: 'single-document-local', id: null, item: item });
        }
    };

    const handleConfirmDelete = async () => {
        // Handle local file deletion immediately
        if (deleteConfirm.type === 'single-document-local' && deleteConfirm.item) {
             const currentDocs = [...(formData.documents || [])];
             const newDocs = currentDocs.filter(d => d !== deleteConfirm.item);
             onChange(newDocs, 'documents');
             handleCloseDelete();
             toast.success(<Toast message={t('common.messages.removedFromList') || 'Removed from list'} />, { icon: false });
             return;
        }

        if (!id) return;

        setDeleteLoading(true);
        try {
            let payloadIds: number[] = [];

            if (deleteConfirm.type === 'gallery') {
                 const gallery = formData.gallery || [];
                 selectedGalleryItems.forEach(index => {
                    const item = gallery[index];
                    if (item && !(item instanceof File) && item.id) {
                        payloadIds.push(item.id);
                    }
                 });
            } else if (deleteConfirm.type === 'documents') {
                const documents = formData.documents || [];
                selectedDocumentItems.forEach(item => {
                   const isLocal = item.file instanceof File || item instanceof File;
                   if (!isLocal && item.id) {
                       payloadIds.push(item.id);
                   }
                });
            } else if (deleteConfirm.id) {
                payloadIds.push(deleteConfirm.id);
            }

            if (payloadIds.length > 0) {
                await PropertyService.deleteDocument({
                    ids: payloadIds,
                    property_id: Number(id)
                });
                toast.success(<Toast message={t('common.messages.filesDeleted') || 'File(s) deleted successfully'} />, { icon: false });
                
                if (onRefresh) {
                     if (deleteConfirm.type === 'gallery') {
                        const currentGallery = [...(formData.gallery || [])];
                        const newGallery = currentGallery.filter((_, idx) => !selectedGalleryItems.includes(idx));
                        onChange(newGallery, 'gallery');
                         setTimeout(() => onRefresh(), 100); 
                         setSelectedGalleryItems([]);
                     } else if (deleteConfirm.type === 'documents') {
                        const currentDocuments = [...(formData.documents || [])];
                        const newDocuments = currentDocuments.filter(doc => !selectedDocumentItems.includes(doc));
                        onChange(newDocuments, 'documents');
                         setTimeout(() => onRefresh(), 100); 
                         setSelectedDocumentItems([]);
                     } else {
                         // For single document delete
                         onRefresh();
                     }
                } else {
                     if (deleteConfirm.type === 'gallery') {
                         const currentGallery = [...(formData.gallery || [])];
                         const newGallery = currentGallery.filter((_, idx) => !selectedGalleryItems.includes(idx));
                         onChange(newGallery, 'gallery');
                         setSelectedGalleryItems([]);
                     } else if (deleteConfirm.type === 'documents') {
                         const currentDocuments = [...(formData.documents || [])];
                         const newDocuments = currentDocuments.filter(doc => !selectedDocumentItems.includes(doc));
                         onChange(newDocuments, 'documents');
                         setSelectedDocumentItems([]);
                     } else if (deleteConfirm.type) {
                        onChange(null, deleteConfirm.type);
                     }
                }
            } else {
                 // Handle cases where we are deleting local files from bulk selection
                 if (deleteConfirm.type === 'gallery') {
                    const currentGallery = [...(formData.gallery || [])];
                    const newGallery = currentGallery.filter((_, idx) => !selectedGalleryItems.includes(idx));
                    onChange(newGallery, 'gallery');
                    setSelectedGalleryItems([]);
                    toast.success(<Toast message={t('common.messages.removedFromList') || 'Removed from list'} />, { icon: false });
                } else if (deleteConfirm.type === 'documents') {
                    const currentDocuments = [...(formData.documents || [])];
                    const newDocuments = currentDocuments.filter(doc => !selectedDocumentItems.includes(doc));
                    onChange(newDocuments, 'documents');
                    setSelectedDocumentItems([]);
                    toast.success(<Toast message={t('common.messages.removedFromList') || 'Removed from list'} />, { icon: false });
                }
            }
            
            handleCloseDelete();
        } catch (error) {
            console.error("Delete failed", error);
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleCloseDelete = () => {
        setDeleteConfirm({ open: false, type: null, id: null, item: null });
    };

    const handleToggleSelect = (index: number) => {
        setSelectedGalleryItems(prev => {
            if (prev.includes(index)) {
                return prev.filter(i => i !== index);
            } else {
                return [...prev, index];
            }
        });
    };

    const handleDeleteSelected = () => {
        if (selectedGalleryItems.length === 0) return;
        setDeleteConfirm({ open: true, type: 'gallery', id: null });
    };

    const handleDocumentToggleSelect = (item: any) => {
        setSelectedDocumentItems(prev => {
            if (prev.includes(item)) {
                return prev.filter(i => i !== item);
            } else {
                return [...prev, item];
            }
        });
    };

    const handleDeleteSelectedDocuments = () => {
        if (selectedDocumentItems.length === 0) return;
        setDeleteConfirm({ open: true, type: 'documents', id: null });
    };

    const documentsHeadCells: HeadCellType[] = useMemo(() => {
        const cells: HeadCellType[] = [
             {
                id: 'name',
                label: t('common.labels.fileName') || 'File Name',
                width: view ? '50%' : '45%',
                sortEnabled: false
            },
            {
                id: 'doc_type',
                label: t('common.labels.documentType') || 'Document Type',
                width: view ? '30%' : '25%',
                sortEnabled: false
            },
            {
                id: 'actions',
                label: t('common.labels.action') || 'Actions',
                width: '100px',
                sortEnabled: false
            }
        ];

        if (!view) {
            cells.unshift({
                id: 'select',
                label: '',
                width: '50px',
                sortEnabled: false
            });
        }

        return cells;
    }, [t, view]);

    const tableValues: TableContextProvider = useMemo(() => {
        return {
            headCells: documentsHeadCells,
            row: DocumentRow,
            data: formData.documents || [],
            loading: false,
            total: formData.documents ? formData.documents.length : 0,
            page: 1,
            perPage: 1000,
            order: 'asc' as any,
            orderBy: 'name',
            setPage: () => {},
            setPerPage: () => {},
            setOrder: () => {},
            setOrderBy: () => {},
            pagination: false,
            noDataFoundMessage: t('common.table.noRecordsFound'),
            rowProps: {
                selectedItems: selectedDocumentItems,
                onToggleSelect: handleDocumentToggleSelect,
                onChange: onChange,
                t: t,
                view: view,
                formData: formData,
                documentTypes: documentTypes,
                errors: errors,
                onDelete: handleSingleDelete
            }
        };
    }, [documentsHeadCells, formData.documents, selectedDocumentItems, view, t, documentTypes, errors]);

    return (
        <div className="card-body">
            <div className="row g-4">
                {/* Primary Image Section */}
                <div className="col-md-4">
                    <h6 className="mb-3">{t('properties.addEdit.pageTexts.form.files.primaryImage.label') || 'Primary Image'}</h6>
                    <ImageUpload
                        name="primary_image"
                        value={formData.primary_image instanceof File ? formData.primary_image : formData.primary_image?.documents?.[0]}
                        previewUrl={formData.primary_image?.documents?.[0]?.doc_url}
                        onChange={(e) => onChange(e, 'primary_image')}
                        disabled={view}
                        label={t('properties.addEdit.pageTexts.form.files.primaryImage.placeholder') || 'Upload Primary Image'}
                        onRemove={() => handleRemoveFile('primary_image')}
                    />
                </div>

                {/* Whatsapp Image Section */}
                <div className="col-md-4">
                    <h6 className="mb-3">{t('properties.addEdit.pageTexts.form.files.whatsappImage.label') || 'Whatsapp Image'}</h6>
                    <ImageUpload
                        name="whatsapp_image"
                        value={formData.whatsapp_image instanceof File ? formData.whatsapp_image : formData.whatsapp_image?.documents?.[0]}
                        previewUrl={formData.whatsapp_image?.documents?.[0]?.doc_url}
                        onChange={(e) => onChange(e, 'whatsapp_image')}
                        disabled={view}
                        label={t('properties.addEdit.pageTexts.form.files.whatsappImage.placeholder') || 'Upload Whatsapp Image'}
                        onRemove={() => handleRemoveFile('whatsapp_image')}
                    />
                </div>

                {/* Brochure Section */}
                <div className="col-md-4">
                    <h6 className="mb-3">{t('properties.addEdit.pageTexts.form.files.brochure.label')}</h6>
                    <FileUpload
                        name="brochure"
                        value={formData.brochure instanceof File ? formData.brochure : formData.brochure?.documents?.[0]?.doc_url}
                        onChange={(e) => onChange(e, 'brochure')}
                        disabled={view}
                        label={t('properties.addEdit.pageTexts.form.files.brochure.placeholder') || 'Upload Brochure'}
                        accept=".pdf"
                        helperText="Allowed PDF only."
                        onRemove={() => handleRemoveFile('brochure')}
                    />
                </div>

                <div className="col-12">
                    <hr className="my-2" />
                </div>

                <div className="col-12">
                    <div className="row align-items-center mb-3">
                        <div className="col">
                            <h6 className="mb-0">{t('properties.addEdit.pageTexts.form.files.gallery.label')}</h6>
                            <small className="text-muted">
                                {selectedGalleryItems.length > 0 
                                    ? <span className="text-primary fw-bold">{selectedGalleryItems.length} {t('common.labels.selected') || 'selected'}</span> 
                                    : t('properties.addEdit.pageTexts.form.files.gallery.subLabel') || 'Manage property gallery images'}
                            </small>
                        </div>
                        <div className="col-auto d-flex gap-2">
                            {selectedGalleryItems.length > 0 && !view && (
                                <Button
                                    className="btn btn-danger text-white"
                                    onClick={handleDeleteSelected}
                                    text={t('common.buttons.deleteSelected', { count: selectedGalleryItems.length }) || `Delete (${selectedGalleryItems.length})`}
                                    icon={<i className="ri-delete-bin-line me-1"></i>}
                                />
                            )}
                            {!view && (
                                <>
                                    <input
                                        ref={galleryInputRef}
                                        type="file"
                                        multiple
                                        accept="image/*"
                                        className="d-none"
                                        onChange={handleGalleryMultipleUpload}
                                    />
                                    <Button
                                        className="btn btn-primary"
                                        onClick={() => galleryInputRef.current?.click()}
                                        text={t('common.buttons.addPhotos') || 'Add Photos'}
                                        icon={<i className="ri-image-add-line me-1"></i>}
                                    />
                                </>
                            )}
                        </div>
                    </div>

                    <div className="d-flex flex-wrap gap-3">
                        {(formData.gallery || []).map((item: any, index: number) => {
                            const isSelected = selectedGalleryItems.includes(index);
                            return (
                                <div 
                                    key={index} 
                                    className={`position-relative shadow-sm rounded overflow-hidden border ${isSelected ? 'border-primary border-2' : ''}`} 
                                    style={{ width: '150px', height: '150px', cursor: !view ? 'pointer' : 'default' }}
                                    onClick={!view ? () => handleToggleSelect(index) : undefined}
                                >
                                    <img
                                        src={item instanceof File ? URL.createObjectURL(item) : item?.doc_url}
                                        alt={`Gallery Item ${index + 1}`}
                                        className="w-100 h-100 object-fit-cover"
                                    />
                                    {!view && (
                                        <div className="position-absolute top-0 start-0 p-2">
                                            <div className={`form-check`} style={{ pointerEvents: 'none' }}>
                                                <input 
                                                    className="form-check-input" 
                                                    type="checkbox" 
                                                    checked={isSelected} 
                                                    readOnly 
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                        
                        {(!formData.gallery || formData.gallery.length === 0) && (
                            <div className="w-100 py-5 text-center border rounded-3 bg-light border-dashed">
                                <i className="ri-images-line fs-1 text-muted mb-2"></i>
                                <p className="text-muted m-0">{t('common.table.noRecordsFound')}</p>
                            </div>
                        )}
                    </div>
                </div>

                <div className="col-12">
                    <hr className="my-2" />
                </div>

                <div className="col-12">
                     <div className="row align-items-center mb-3">
                        <div className="col">
                            <h6 className="mb-0">{t('properties.addEdit.pageTexts.form.files.documents.label') || 'Documents'}</h6>
                            <small className="text-muted">
                                {t('properties.addEdit.pageTexts.form.files.documents.subLabel') || 'Upload other related documents (e.g. Legal, Floor Plans, etc.)'}
                                {selectedDocumentItems.length > 0 && <span className="text-primary fw-bold ms-2">{selectedDocumentItems.length} {t('common.labels.selected') || 'selected'}</span>}
                            </small>
                        </div>
                        <div className="col-auto d-flex gap-2">
                            {selectedDocumentItems.length > 0 && !view && (
                                <Button
                                    className="btn btn-danger text-white"
                                    onClick={handleDeleteSelectedDocuments}
                                    text={t('common.buttons.deleteSelected', { count: selectedDocumentItems.length }) || `Delete (${selectedDocumentItems.length})`}
                                    icon={<i className="ri-delete-bin-line me-1"></i>}
                                />
                            )}
                            {!view && (
                                <>
                                    <input
                                        type="file"
                                        multiple
                                        className="d-none"
                                        id="document-upload-input"
                                        accept=".xls,.xlsx,.doc,.docx,.txt,.pdf,.jpg,.jpeg,.png,.gif,.bmp,.webp,image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain"
                                        onChange={(e) => {
                                            if (e.target.files && e.target.files.length > 0) {
                                                // Define allowed file types
                                                const allowedExtensions = ['.xls', '.xlsx', '.doc', '.docx', '.txt', '.pdf', '.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'];
                                                const allowedMimeTypes = [
                                                    'application/vnd.ms-excel',
                                                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                                                    'application/msword',
                                                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                                                    'text/plain',
                                                    'application/pdf',
                                                    'image/jpeg',
                                                    'image/png',
                                                    'image/gif',
                                                    'image/bmp',
                                                    'image/webp'
                                                ];

                                                const validFiles: File[] = [];
                                                const invalidFiles: string[] = [];

                                                Array.from(e.target.files).forEach((file) => {
                                                    const fileName = file.name.toLowerCase();
                                                    const fileExtension = fileName.substring(fileName.lastIndexOf('.'));
                                                    const isValidExtension = allowedExtensions.includes(fileExtension);
                                                    const isValidMimeType = allowedMimeTypes.includes(file.type);

                                                    if (isValidExtension || isValidMimeType) {
                                                        validFiles.push(file);
                                                    } else {
                                                        invalidFiles.push(file.name);
                                                    }
                                                });

                                                if (invalidFiles.length > 0) {
                                                    toast.error(
                                                        <Toast 
                                                            message={`Invalid file type(s): ${invalidFiles.join(', ')}. Only Excel, Word, PDF, Text, and Image files are allowed.`} 
                                                        />, 
                                                        { icon: false }
                                                    );
                                                }

                                                if (validFiles.length > 0) {
                                                    const timestamp = Date.now();
                                                    const newFiles = validFiles.map((file, idx) => ({
                                                        file,
                                                        doc_type: '', 
                                                        name: file.name,
                                                        _tempId: `temp-${timestamp}-${idx}`
                                                    }));
                                                    const currentDocs = formData.documents || [];
                                                    onChange([...currentDocs, ...newFiles], 'documents');
                                                }

                                                e.target.value = '';
                                            }
                                        }}
                                    />
                                    <Button
                                        className="btn btn-primary"
                                        onClick={() => document.getElementById('document-upload-input')?.click()}
                                        text={t('common.buttons.addDocuments') || 'Add Documents'}
                                        icon={<i className="ri-file-add-line me-1"></i>}
                                    />
                                </>
                            )}
                        </div>
                    </div>

                    <TableBox tableValues={tableValues} />
                </div>
            </div>

            <Modal
                open={deleteConfirm.open}
                onClose={handleCloseDelete}
                title={t('common.modal.deleteConfirmationTitle') || 'Delete Confirmation'}
                maxWidth="sm"
                content={
                    <div className="text-center">
                        <i className="ri-error-warning-line text-warning fs-1 mb-3"></i>
                        <p className="mb-0">{t('common.modal.deleteConfirmationMessage') || 'Are you sure you want to delete this file? This action cannot be undone.'}</p>
                    </div>
                }
                action={
                    <div className="d-flex justify-content-center gap-3 w-100 pb-3">
                         <Button
                            className="btn btn-outline-secondary"
                            onClick={handleCloseDelete}
                            text={t('common.buttons.cancel') || 'Cancel'}
                            disabled={deleteLoading}
                        />
                        <Button
                            className="btn btn-danger"
                            onClick={handleConfirmDelete}
                            text={t('common.buttons.delete') || 'Delete'}
                            loading={deleteLoading}
                            disabled={deleteLoading}
                        />
                    </div>
                }
            />
        </div>
    );
};

export default FilesTab;
